import { Component, HostListener, inject, signal, type OnDestroy, type OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router, type ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, Subject, take, takeUntil, withLatestFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../authentication/_services/auth.service';
import { NotificationService } from '../../../modules/notifications/notification.service';
import { type Language, type UserInfo } from '../../../modules/user-management/users/user.model';
import { UserService } from '../../../modules/user-management/users/user.service';
import { PasswordExpiryService } from '../../components/password-expiry-modal/services/password-expiry.service.component';
import { CustomToastrService } from '../../services/custom-toastr.service';
import { LanguageService } from '../../services/language.service';
import { ResponsiveService } from '../../services/responsive.service';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  styleUrl: './full.component.scss',
  standalone: false
})
export class FullComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  responsive = inject(ResponsiveService);
  private languageService = inject(LanguageService);
  private customToastr = inject(CustomToastrService);
  private translationService = inject(TranslationService);
  private notificationService = inject(NotificationService);
  passwordExpiryService = inject(PasswordExpiryService);

  private readonly _userInfo = toSignal(this.authService.userInfo$, { initialValue: null });
  private readonly _unreadCount = this.notificationService.unreadCount;

  get userInfo(): UserInfo | null {
    return this._userInfo();
  }

  get unreadNotificationCount(): number {
    return this._unreadCount();
  }

  get notificationBadgeLabel(): string {
    const count = this._unreadCount();
    return count > 99 ? '99+' : `${count}`;
  }

  languages = signal<Language[]>([]);
  languageControl = new FormControl('');
  isSidenavOpen = false;
  screenWidth!: number;
  erpLogoUrl = environment.erpLogoUrl;
  codatronLogoUrl = environment.codatronLogoUrl;
  isApplicationSupport = false;

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.authService.isLoggedIn$
      .pipe(
        filter(isLoggedIn => isLoggedIn === true),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.initializeLanguageControl();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.responsive.isHandset()) {
          this.isSidenavOpen = false;
        }
      });
  }

  get userName(): string {
    return this.userInfo ? [this.userInfo.fName, this.userInfo.lName].filter(Boolean).join(' ') : '';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.screenWidth = event.target.innerWidth;
  }

  async ngOnInit(): Promise<void> {
    this.isApplicationSupport = this.authService.getUserRoles().includes('ApplicationSupport');
    this.languageService.getLanguagesForDropdown().subscribe({
      next: (res: Language[]) => {
        this.languages.set(res);
      }
    });

    this.authService.isLoggedIn$
      .pipe(
        takeUntil(this.destroy$),
        filter(isLoggedIn => isLoggedIn === true),
        withLatestFrom(this.authService.userInfo$),
        filter(([, info]) => info === null),
        take(1)
      )
      .subscribe(() => {
        this.userService.getLoggedInUserProfile().subscribe({
          next: profile => {
            this.authService.updateCurrentUserInfo(profile);
            this.authService.updateLocalProfile(profile);
          }
        });
      });

    this.initializeComponent();
    this.initializeLanguageControl();
    this.setupLanguageControlListener();
    this.setupNotificationListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidenav(): void {
    this.isSidenavOpen = !this.isSidenavOpen;
  }

  onSidenavOpenedChange(opened: boolean): void {
    if (this.responsive.isHandset()) {
      this.isSidenavOpen = opened;
    }
  }

  onLogout(): void {
    this.authService.logout();
  }

  private initializeComponent(): void {
    this.screenWidth = window.innerWidth;
  }

  private initializeLanguageControl(): void {
    const currentLang = this.translate.currentLang;
    this.languageControl.setValue(currentLang, { emitEvent: false });
  }

  private setupLanguageControlListener(): void {
    this.languageControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(lang => {
      if (lang && lang !== this.translate.currentLang) {
        this.translationService.setLanguage(lang);
      }
    });
  }

  getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getCurrentLanguageLabel(): string {
    const currentLang = this.translate.currentLang;
    const language = this.languages().find(lang => lang.code === currentLang);
    return language ? language.name : 'UNKNOWN';
  }

  setupNotificationListener(): void {
    this.notificationService.connect();

    if (!this.passwordExpiryService.hasPasswordExpired()) {
      this.notificationService.getUnreadCount().subscribe();
    }
  }

  redirectToHomePage(): void {
    this.customToastr.success(this.translate.instant('COMMON.REDIRECTING_TO_HOME_PAGE'));
    setTimeout(() => {
      window.open(environment.orgUrl, '_blank');
    }, 2000);
  }

  viewProfile(): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/users/profile/view'], {
      queryParams: { returnUrl: currentUrl }
    });
  }

  changePassword(): void {
    this.router.navigate(['/change-password']);
  }

  redirectToCodatron(): void {
    this.customToastr.success(this.translate.instant('COMMON.CODATRON_HOMEPAGE'));
    setTimeout(() => {
      window.open(environment.codatronUrl, '_blank');
    }, 2000);
  }
}
